import {AuthorityType, PAYOUT_PURPOSE_CODES_BY_AUTHORITY, PayoutPurposeCode} from '@sollapay/enums';

import {PayoutAuthorityCreditorInvalidError} from '../errors';
import {isBankCheckTransfer} from '../rules';

import type {PayoutTransferType} from '@sollapay/enums';

/** What a caller submits for an authority payout, before it becomes a stored creditor row. */
export interface AuthorityPayoutCreditorInput {
  authorityType: AuthorityType;
  purposeCode: PayoutPurposeCode;
  purposeOtherDetail?: string | null;
  municipalityName?: string | null;
  voucherNumber?: string | null;
  paymentReference?: string | null;
  accountNumber?: string | null;
  transferType?: PayoutTransferType | null;
}

/** Authorities that are paid by bank transfer, and so must carry a creditor account. */
const TRANSFER_PAID_AUTHORITIES: readonly AuthorityType[] = [
  AuthorityType.MUNICIPALITY,
  AuthorityType.ISRAEL_LAND_AUTHORITY
];

export class PayoutAuthorityPolicy {
  /**
   * Every field an authority payout may carry is only meaningful for some of the three
   * authorities, so each one is asserted both present-when-required and absent-when-irrelevant —
   * a voucher number on a municipality payout means the caller mixed up the payee kind.
   */
  static assertValidCreditor(creditor: AuthorityPayoutCreditorInput): void {
    const {authorityType} = creditor;

    this.assertPurposeCodeAllowed(creditor);
    this.assertPurposeOtherDetail(creditor);

    if (authorityType === AuthorityType.TAX_AUTHORITY) {
      this.assertPresent(creditor.voucherNumber, 'voucherNumber', authorityType);
      this.assertAbsent(creditor.paymentReference, 'paymentReference', authorityType);
      this.assertAbsent(creditor.accountNumber, 'accountNumber', authorityType);
    } else {
      this.assertPresent(creditor.paymentReference, 'paymentReference', authorityType);
      this.assertAbsent(creditor.voucherNumber, 'voucherNumber', authorityType);
    }

    if (
      TRANSFER_PAID_AUTHORITIES.includes(authorityType) &&
      !isBankCheckTransfer(creditor.transferType)
    ) {
      this.assertPresent(creditor.accountNumber, 'accountNumber', authorityType);
    }

    if (authorityType === AuthorityType.MUNICIPALITY) {
      this.assertPresent(creditor.municipalityName, 'municipalityName', authorityType);
    } else {
      this.assertAbsent(creditor.municipalityName, 'municipalityName', authorityType);
    }
  }

  private static assertPurposeCodeAllowed(creditor: AuthorityPayoutCreditorInput): void {
    const allowed = PAYOUT_PURPOSE_CODES_BY_AUTHORITY[creditor.authorityType];
    if (!allowed.includes(creditor.purposeCode)) {
      throw new PayoutAuthorityCreditorInvalidError(
        `Purpose code ${creditor.purposeCode} is not valid for ${creditor.authorityType}`
      );
    }
  }

  private static assertPurposeOtherDetail(creditor: AuthorityPayoutCreditorInput): void {
    const detail = creditor.purposeOtherDetail?.trim();

    if (creditor.purposeCode === PayoutPurposeCode.OTHER) {
      if (!detail) {
        throw new PayoutAuthorityCreditorInvalidError(
          'purposeOtherDetail is required when the purpose code is OTHER'
        );
      }
      return;
    }

    if (detail) {
      throw new PayoutAuthorityCreditorInvalidError(
        `purposeOtherDetail is only valid when the purpose code is OTHER, got ${creditor.purposeCode}`
      );
    }
  }

  private static assertPresent(
    value: string | null | undefined,
    field: string,
    authorityType: AuthorityType
  ): void {
    if (!value?.trim()) {
      throw new PayoutAuthorityCreditorInvalidError(`${field} is required for ${authorityType}`);
    }
  }

  private static assertAbsent(
    value: string | null | undefined,
    field: string,
    authorityType: AuthorityType
  ): void {
    if (value?.trim()) {
      throw new PayoutAuthorityCreditorInvalidError(
        `${field} is not valid for ${authorityType} payouts`
      );
    }
  }
}
